import moment from 'jalali-moment';
import { LegacyRef, ReactElement, useEffect, useRef, useState } from 'react';
import ReactPaginate from 'react-paginate';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import endpoints from '../../config/endpoints';
import globalEnum from '../../enums/globalEnum';
import { popupSlice } from '../../services/reducers';
import { setLoading } from '../../services/reducers/homeSlice';
import { orders } from '../../services/reducers/userSlice';
import restApi from '../../services/restApi';
import { useAppSelector } from '../../services/store';
import tools from '../../utils/tools';
import { Sidebar } from '../Layout/Sidebar';
import Bijack from './Bijack';
import Bill from './Bill';
import Status from './Status';
import BillDetail from './‌BillDetail';
import userRole = globalEnum.userRole;
import orderStatus = globalEnum.orderStatus;

// Fiscal-year tabs, computed from the current Jalali year at render time
// (same convention as Analytics/AnalyticsLayout.tsx's "سال مالی <جاری>"/
// "سال مالی <قبلی>" presets) so this stays correct without yearly upkeep.
const currentJalaliYear = () => moment().format('jYYYY');
const lastJalaliYear = () => moment().subtract(1, 'jYear').format('jYYYY');

const Orders = () => {
  const [data, setData] = useState<any>([]);
  const [workers, setWorkers] = useState([]);
  const [query, setQuery] = useState('');
  const [tab, setTab] = useState('all');
  const [yearTab, setYearTab] = useState(currentJalaliYear());
  const [itemOffset, setItemOffset] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const itemsPerPage = 25;
  const userReducer = useAppSelector(state => state.userReducer);
  const endOffset = (itemOffset || 0) + itemsPerPage;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Helper function to get role-specific tab titles
  const getTabTitlesForRole = () => {
    if (userReducer?.profile?.role === userRole.bearingCommerce) {
      return {
        all: 'همه',
        WaitForCommerceBearingApproval: 'در انتظار تایید بازرگانی بلبرینگ',
        UnapprovedCommerceBearing: 'عدم تایید بازرگان بلبرینگ',
        Sent: 'ارسال شده',
      };
    } else if (userReducer?.profile?.role === userRole.feltBowlCommerce) {
      return {
        all: 'همه',
        WaitForCommerceFeltBowlApproval: 'در انتظار تایید بازرگانی کاسه نمد',
        UnapprovedCommerceFeltBowl: 'عدم تایید بازرگان کاسه نمد',
        Sent: 'ارسال شده',
      };
    } else if (userReducer?.profile?.role === userRole.warehouse) {
      return {
        all: 'همه',
        WaitForWarehouseApproval: 'در انتظار تایید انبار',
        // WarehouseApproved: 'تایید شده توسط انبار',
      };
    } else {
      return {
        all: 'همه',
        WaitForCommerceBearingApproval: 'در انتظار تایید بازرگانی بلبرینگ',
        WaitForCommerceFeltBowlApproval: 'در انتظار تایید بازرگانی کاسه نمد',
        WaitForWarehouseApproval: 'در انتظار تایید انبار',
        // WarehouseApproved: 'تایید شده توسط انبار',
        Sent: 'ارسال شده',
        UnapprovedCommerceBearing: 'عدم تایید بازرگان بلبرینگ',
        UnapprovedCommerceFeltBowl: 'عدم تایید بازرگان کاسه نمد',
      };
    }
  };

  const tabTitles = getTabTitlesForRole();

  // Helper function to calculate role-specific price
  const calculateRoleSpecificPrice = (order: any) => {
    if (!order.orderProducts || order.orderProducts.length === 0) {
      return 0;
    }

    let total = 0;

    for (const orderProduct of order.orderProducts) {
      const category = orderProduct?.product?.category;

      // For bearing commerce, only count non-feltbowl products
      if (userReducer?.profile?.role === userRole.bearingCommerce) {
        if (category?.slug !== 'کاسه-نمد') {
          total += (orderProduct.count * orderProduct.itemsPerUnitType * orderProduct.singlePrice);
        }
      }
      // For feltbowl commerce, only count feltbowl products
      else if (userReducer?.profile?.role === userRole.feltBowlCommerce) {
        if (category?.slug === 'کاسه-نمد') {
          total += (orderProduct.count * orderProduct.itemsPerUnitType * orderProduct.singlePrice);
        }
      }
      // For other roles, show full price
      else {
        total += (orderProduct.count * orderProduct.itemsPerUnitType * orderProduct.singlePrice);
      }
    }

    return total;
  };

  // Orders are scoped to the selected fiscal year first — the status tabs
  // below (and their counts) then filter/count only within that year.
  const yearFilteredData = data?.filter((e: any) => e?.placedDate && moment(e.placedDate).format('jYYYY') === yearTab);

  const filteredData = yearFilteredData?.sort((a, b) => moment(b?.placedDate).unix() - moment(a?.placedDate).unix())?.filter((e: any) => {
    switch (tab){
      case('WaitForCommerceBearingApproval'):
        return e.status == orderStatus.WaitForCommerceBearingApproval;
      case('WaitForCommerceFeltBowlApproval'):
        return e.status == orderStatus.WaitForCommerceFeltBowlApproval;
      case('WaitForWarehouseApproval'):
        return e.status == orderStatus.WaitForWarehouseApproval;
      // case('WarehouseApproved'):
      //   return e.status == orderStatus.WarehouseApproved;
      case('Sent'):
        return e.status == orderStatus.Sent;
      case('UnapprovedCommerceBearing'):
        return e.status == orderStatus.UnapprovedCommerceBearing;
      case('UnapprovedCommerceFeltBowl'):
        return e.status == orderStatus.UnapprovedCommerceFeltBowl;
      default:
        return true;
    }
  })?.filter(e => e.user?.name?.toLowerCase()?.includes(query?.toLowerCase()) || e.user?.lastName?.toLowerCase()?.includes(query?.toLowerCase()) || e.user?.phoneNumber?.toLowerCase()?.includes(query?.toLowerCase()) || e?.code?.toLowerCase()?.includes(query?.toLowerCase()));

  let currentItems = filteredData?.slice(itemOffset, endOffset);
  const pageCount = Math.ceil(filteredData?.length / itemsPerPage);

  const deleteOrder = async (id: number) => {
    if(confirm('آیا مطمئن هستید؟')){
      dispatch(setLoading(true));

      const res = await restApi(process.env.REACT_APP_BASE_URL + '/admin/order/' + id, true).delete({});

      if(res.code == 204){
        fetchData()
        Swal.fire({
          title: 'موفق',
          text: 'سفارش با موفقیت حذف شد',
          icon: 'success',
          confirmButtonText: 'متوجه شدم'
        })
      } else {
        Swal.fire({
          title: 'ناموفق',
          text: res?.data,
          icon: 'error',
          confirmButtonText: 'متوجه شدم'
        })
      }

      dispatch(setLoading(false));
    }
  };

  const list = () => {
    const rows: ReactElement[] = [];

    currentItems?.map((order: any, index) => {
      rows.push(
        <tr
          className={`dashTr2 ${index % 2 === 0 ? 'table-row-even' : 'table-row-odd'}`}
          key={'order' + index}
        >
          <td className="svgContainer">
            {userReducer?.profile?.role != userRole.warehouse &&
                <i className="trash clickable" onClick={ () => deleteOrder(order.id) }></i>
            }
            <i className="edit clickable" onClick={ () => navigate(`/order/edit${userReducer?.profile?.role == userRole.warehouse ? '/warehouse' : ''}/` + order.id) }></i>
            <i className="orderInvoice clickable" onClick={() => navigate('/order/invoice/' + order.id)}></i>
            <i className="attachmentSvg clickable" onClick={() => dispatch(popupSlice.middle(<Bijack orderId={order.id} />))}></i>
          </td>
          {userReducer?.profile?.role != userRole.warehouse &&
              <td className="">
                { tools.formatPrice(
                  (userReducer?.profile?.role === userRole.bearingCommerce ||
                    userReducer?.profile?.role === userRole.feltBowlCommerce)
                    ? calculateRoleSpecificPrice(order)
                    : order.price
                ) }
              </td>
          }
          <td>
            {tabTitles[order.status]}
          </td>
          <td className="">{order?.user?.name}</td>
          <td className="">
            <p>{order?.placedDate ? moment(order?.placedDate).format('jYYYY/jMM/jDD HH:MM') : "-"}</p>
          </td>
          <td className="">{order?.code}</td>
        </tr>
      )
    })

    return rows;
  };

  const fetchData = async () => {
    dispatch(setLoading(true));

    await Promise.all([
      await restApi(endpoints.order.index, true).get(),
      await restApi(endpoints.user.index, true).get({ role: 'WORKER'}),
      await restApi(process.env.REACT_APP_BASE_URL + '/order/status', true).get(),
    ]).then((res) => {
      setData(res[0].data);
      setWorkers(res[1].data);
    })

    dispatch(setLoading(false));
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const curPage = searchParams.get('page')
    if (curPage){
      if (Number(curPage) > pageCount){
        setSearchParams({['page']: '1'})
      }
      setItemOffset(((Number(searchParams.get('page')) - 1) * itemsPerPage) % data.length)
    }
  }, [data]);

  return (
    <>
      <body className="dashboardBody">
      <Sidebar />
      <main className="dashBoardMain">
        <h1 className="dashBoardTitle">سفارش ها</h1>
        <div className="dashTabs inventory">
          { [
            { key: currentJalaliYear(), label: `سال مالی ${currentJalaliYear()}` },
            { key: lastJalaliYear(), label: `سال مالی ${lastJalaliYear()}` },
          ].map(({ key, label }) =>
              <span className={ `ordersTag inventory clickable ${ key == yearTab ? 'active' : '' }` } onClick={ () => setYearTab(key) } key={key}>
        { label }
      </span>
          ) }
        </div>
        <div className="dashTabs">
          { Object.entries(tabTitles).map(([key, value]) =>
              <span className={ `ordersTag clickable ${ key == tab ? 'activeTab' : '' }` } onClick={ () => setTab(key) } key={key}>
        { value }
                <span className={ `numberTag ${ key == tab ? 'activeTab' : '' }` }>{ yearFilteredData?.filter((e: any) => {
                  switch (key) {
                    case('WaitForCommerceBearingApproval'):
                      return e.status == orderStatus.WaitForCommerceBearingApproval;
                    case('WaitForCommerceFeltBowlApproval'):
                      return e.status == orderStatus.WaitForCommerceFeltBowlApproval;
                    case('WaitForWarehouseApproval'):
                      return e.status == orderStatus.WaitForWarehouseApproval;
                    // case('WarehouseApproved'):
                    //   return e.status == orderStatus.WarehouseApproved;
                    case('Sent'):
                      return e.status == orderStatus.Sent;
                    case('UnapprovedCommerceBearing'):
                      return e.status == orderStatus.UnapprovedCommerceBearing;
                    case('UnapprovedCommerceFeltBowl'):
                      return e.status == orderStatus.UnapprovedCommerceFeltBowl;
                    default:
                      return true;
                  }
                }).length }</span>
      </span>
          ) }
        </div>
        <div className="searchContainer">
          <span className="backSpan keepRight">
           <span className="dashboardHeader clickable" onClick={() => navigate('/order/add')}>
            <p>ثبت سفارش جدید</p>
            <i className="plusIcon"></i>
          </span>
            </span>
          <div className="dashboardseaechBox">
            <i className="dashMagnifierIcon"></i>
            <input className="dashSearchInput" placeholder="جستجو" onChange={(input: any) => setQuery(input.target.value)}></input>
          </div>
        </div>
        <table className="alternating-table">
          <thead>
          <tr className="dashTr1 blueText">
            <th>عملیات</th>
            {userReducer?.profile?.role != userRole.warehouse &&
                <th className="">مبلغ کل</th> }
            <th className="">وضعیت سفارش</th>
            <th>کاربر</th>
            <th className="">تاریخ</th>
            <th>شماره سفارش</th>
          </tr>
          </thead>
          <tbody>
          {list()}
          </tbody>
        </table>
        <ReactPaginate
          breakLabel="..."
          nextLabel="بعدی >"
          onPageChange={(event) => {
            setSearchParams({['page']: (Number(event.selected) + 1).toString()})
            setItemOffset((event.selected * itemsPerPage) % data.length);
          }}
          initialPage={Number(searchParams.get('page')) - 1}
          pageRangeDisplayed={5}
          pageCount={pageCount}
          previousLabel="< قبلی"
          renderOnZeroPageCount={null}
          className="pagination"
          pageClassName="paginationBreak"
          previousClassName="paginationBreak"
          nextClassName="paginationBreak"
          activeClassName="paginationActive"
        />
      </main>
      </body>
    </>
  )
}

export default Orders